import type { RoutePoint } from '@/types/route-simulation';
import type { DabeeoMapData } from 'dabeeomaps';
import type { IClosestPoiDataOption } from 'dabeeomaps/dist/src/model/IDataOption';
import type { IPoi } from 'dabeeomaps/dist/src/model/map/IPoi';
import { generateGetRouteOptions } from './route-simulation';
import pLimit from 'p-limit';
import type { NearbyPoiItemType, NearbyPoiItemWithDistanceType } from '@/types/map';

type LanguageText = { lang: string; text: string };

/**
 * Get POI title by language with fallback
 * Priority: current language → Korean → default title
 */
export const getPoiTitleByLanguage = (
    poi: { titleByLanguages?: LanguageText[]; title?: string } | undefined,
    language: string
): string => {
    if (!poi) return '';
    return (
        poi.titleByLanguages?.find((item) => item.lang === language)?.text ??
        poi.titleByLanguages?.find((item) => item.lang === 'ko')?.text ??
        poi.title ??
        ''
    );
};

export const getPoiById = (poiList?: IPoi[], poiId?: string) => {
    if (!Array.isArray(poiList) || !poiId) {
        return undefined;
    }

    return poiList.find((poi) => poi?.id === poiId);
};

/**
 * Helper function to calculate endPoint from POI
 * Can be used synchronously without waiting for state update
 */
export const calculateEndPointFromPoi = (poi: IPoi | any | undefined, mapData?: DabeeoMapData): RoutePoint => {
    const allFloors = mapData?.dataFloor?.getFloors();
    const floor = allFloors?.find((item: any) => item.id === poi?.floorId);
    const floorName = floor?.name?.find((n: any) => n.lang === 'ko')?.text || '';
    
    return {
        floorId: poi?.floorId || '',
        floorName: floorName,
        position: {
            x: poi?.position?.x || 0,
            y: poi?.position?.y || 0,
        },
        poiId: poi?.id,
    };
};

function getDistance(p1: { x: number; y: number }, p2: { x: number; y: number }) {
    return Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
}

export const getCurrentPois = (mapData: DabeeoMapData | undefined, options: IClosestPoiDataOption) => {
    const currentPoiList =
        (mapData?.dataPoi?.find({
            floorId: options.floorId,
            categoryCode: options.categoryCode,
        }) as IPoi[]) || [];

    const sortedPois: IPoi[] = currentPoiList
        .map((poi) => ({
            poi,
            distance: getDistance(options.position || { x: 0, y: 0 }, poi.position), // 거리 계산 결과를 함께 저장
        }))
        .sort((a, b) => a.distance - b.distance) // 거리 기준 오름차순으로 정렬
        .map((item) => item.poi);

    return sortedPois;
};
/**
 * Helper function: Đợi POI được load xong sau khi đổi floor
 * Sử dụng RAF + exponential backoff để tối ưu performance
 */
export const waitForPoisToLoad = async (mapData: DabeeoMapData | undefined, options: IClosestPoiDataOption, debugContext?: string): Promise<IPoi[]> => {
    let currentPoiList: IPoi[] = [];

    // Tier 1: Thử ngay lập tức với RequestAnimationFrame (frame-perfect timing)
    await new Promise<void>((resolve) => {
        let rafCount = 0;
        const maxRAF = 3; // Thử 3 frames đầu (~50ms)

        const checkRAF = () => {
            currentPoiList = getCurrentPois(mapData, options);
            if (currentPoiList.length > 0) {
                if (debugContext) {
                    console.log(`✅ [${debugContext}] POI loaded via RAF (${rafCount} frames):`, currentPoiList.length);
                }
                resolve();
                return;
            }

            rafCount++;
            if (rafCount < maxRAF) {
                requestAnimationFrame(checkRAF);
            } else {
                resolve();
            }
        };

        requestAnimationFrame(checkRAF);
    });

    // Tier 2: Nếu RAF không đủ, fallback sang setTimeout với exponential backoff
    if (currentPoiList.length === 0) {
        let retryCount = 0;
        const maxRetries = 15;
        const retryDelays = [100, 100, 150, 200, 250, 300]; // ms

        while (retryCount < maxRetries && currentPoiList.length === 0) {
            const delay = retryDelays[retryCount] || 300;
            await new Promise((resolve) => setTimeout(resolve, delay));

            currentPoiList = (mapData?.dataPoi?.findClosestPois?.(options) as IPoi[]) || [];

            if (currentPoiList.length > 0) {
                if (debugContext) {
                    console.log(`✅ [${debugContext}] POI loaded after ${retryCount + 1} retries:`, currentPoiList.length);
                }
                break;
            }

            retryCount++;
        }
    }

    if (currentPoiList.length === 0 && debugContext) {
        console.warn(`⚠️ [${debugContext}] POI data not loaded after max retries`);
    }

    return currentPoiList;
};

export const calculateGetCurrentPoiOptions = (poi: IPoi): IClosestPoiDataOption => {
    return {
        floorId: poi?.floorId,
        position: {
            x: poi?.position?.x ?? 0,
            y: poi?.position?.y ?? 0,
        },
    };
};

/**
 * @remarks
 * - The `distance` property represents the total route distance in **meters**.
 * - If `poiList` is not an array or is empty, an empty array is returned.
 * - If `mapData` is not provided, the original `poiList` is returned unchanged.
 * - Errors during distance calculation are caught, logged, and the original `poiList` is returned.
 */
export const addDistanceToPoiList = async (
    startPoint: RoutePoint,
    poiList?: NearbyPoiItemType[],
    mapData?: DabeeoMapData
): Promise<NearbyPoiItemWithDistanceType[]> => {
    if (!Array.isArray(poiList) || poiList.length === 0) {
        return [];
    }
    if (!mapData) {
        return poiList;
    }

    const limit = pLimit(5);

    try {
        const results = await Promise.all(
            poiList.map((poi) =>
                limit(async () => {
                    const endPoint: RoutePoint = {
                        floorId: poi?.floorId,
                        position: {
                            x: poi?.position?.x ?? 0,
                            y: poi?.position?.y ?? 0,
                        },
                        poiId: poi?.id,
                    };

                    const options = generateGetRouteOptions(startPoint, endPoint);
                    const directions = await mapData.getRoute(options);

                    return {
                        ...poi,
                        // directions?.recommendation?.totalDistance is measured in centimeters
                        distance: (directions?.recommendation?.totalDistance ?? 0) / 100, // convert to meters
                    };
                })
            )
        );

        return results;
    } catch (error) {
        console.error('Failed to calculate distances:', error);
        return poiList;
    }
};

export const sortNearbyPoiListByDistance = (nearbyPoiList: NearbyPoiItemWithDistanceType[], direction: 'asc' | 'desc' = 'asc') => {
    if (!Array.isArray(nearbyPoiList) || nearbyPoiList.length === 0) {
        return [];
    }

    return nearbyPoiList.sort((a, b) => (direction === 'asc' ? (a.distance ?? 0) - (b.distance ?? 0) : (b.distance ?? 0) - (a.distance ?? 0)));
};
